import { apiRequest } from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';
import { AIAgent } from '../../types';

// 組織の基本情報の型定義
export type PlanType = 'demo' | 'personal' | 'team' | 'agency' | 'enterprise';

export interface Organization {
  id: number;
  name: string;
  description: string | null;
  logo_path?: string | null;
  plan?: PlanType | null;
  /**
   * 手動プランロックの打刻。値がある間は「Stripe の請求プランとアプリ上のプランが
   * 意図的に食い違っている（請求額据え置きで上位プランの機能を開放している）」状態で、
   * Stripe Webhook からのプラン上書きが見送られる。
   */
  plan_locked_at?: string | null;
  member_limit: number;
  monthly_token_limit: number;
  monthly_credit_limit?: number | null;
  daily_free_credits?: number;
  is_active: boolean;
  cancelled_at?: string | null;
  enable_ai_knowledge?: boolean;
  enable_product_catalog?: boolean;
  enable_user_created_agents?: boolean;
  enable_super_agent?: boolean;
  enable_api_integration?: boolean;
  enable_video_material?: boolean;
  max_concurrent_image_jobs?: number;
  max_product_registrations?: number | null;
  enable_competitor_monitoring?: boolean;
  /**
   * 競合監視（LP の自動巡回）を管理者が止めた打刻。値がある間はスケジューラが
   * この組織のサイトを dispatch しない。null なら巡回中。
   */
  competitor_monitoring_disabled_at: string | null;
  /** 巡回対象（status=active）の監視サイト数。 */
  active_monitored_site_count: number;
  enable_ad_score?: boolean;
  enable_lp_creation?: boolean;
  enable_music_creation?: boolean;
  enable_carousel_creation?: boolean;
  enable_movie_creation?: boolean;
  enable_song_ad_creation?: boolean;
  enable_easy_video?: boolean;
  enable_ugc_ad_creation?: boolean;
  enable_ai_doc3?: boolean;
  enable_chat_tools?: boolean;
  enable_ad_operations?: boolean;
  enable_news?: boolean;
  enable_auto_banner_ab_test?: boolean;
  total_users?: number;
  leader_count?: number;
  member_count?: number;
  available_slots?: number;
  monthly_tokens_used?: number;
  remaining_tokens?: number;
  monthly_credits_used?: number;
  remaining_credits?: number;
  created_at: string;
  updated_at: string;
}

// Credit wallet info returned from backend
export interface CreditWalletInfo {
  monthly: {
    balance: number;
    limit: number;
    expires_at: string | null;
  };
  free: {
    balance: number;
    daily_amount: number;
    expires_at: string | null;
  };
  additional: {
    balance: number;
  };
}

// Credit transaction record
export interface CreditTransaction {
  id: number;
  organization_id: number;
  user_id: number | null;
  wallet_type: 'monthly' | 'free' | 'additional';
  transaction_type: 'grant' | 'consume' | 'reset' | 'expire';
  amount: number;
  balance_after: number;
  description: string | null;
  ai_usage_log_id: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

// 組織メンバーの型定義
export interface OrganizationMember {
  id: number;
  name: string;
  email: string;
  role: 'leader' | 'member';
  photo_url: string | null;
  ai_usage_score: number;
  created_at: string;
}

// 組織のAIエージェント設定の型定義
export interface OrganizationAgent extends AIAgent {
  is_enabled_for_organization: boolean;
}

// APIレスポンス型
interface ApiResponse<T> {
  data: T;
  message?: string;
}

// エージェント設定の更新リクエスト型定義
export interface AgentSettingsUpdateRequest {
  agent_settings: {
    agent_id: number;
    is_enabled: boolean;
  }[];
}

/**
 * 管理者用組織一覧取得
 */
export const fetchAdminOrganizations = async (): Promise<Organization[]> => {
  try {
    // apiRequest.getはすでにdata部分を返すので、レスポンスは{ data: { organizations: [...] } }の形式
    const response = await apiRequest.get<{ data: { organizations: Organization[] } }>('/admin/organizations');
    console.log('adminOrganizationService: 組織一覧レスポンス', response);
    
    if (response && response.data && response.data.organizations) {
      return response.data.organizations;
    } else {
      console.error('adminOrganizationService: 不正なレスポンス形式', response);
      throw new Error('APIのレスポンス形式が期待と異なります');
    }
  } catch (error) {
    console.error('adminOrganizationService: エラー発生', error);
    throw handleApiError(error, '組織一覧の取得に失敗しました');
  }
};

/**
 * 管理者用組織詳細取得
 * @param id 組織ID
 */
export const fetchAdminOrganizationDetails = async (id: number): Promise<{
  organization: Organization;
  members: OrganizationMember[];
  agents: OrganizationAgent[];
}> => {
  try {
    // デバッグ用のログ追加
    console.log('adminOrganizationService: 組織詳細取得開始', id);
    
    // APIリクエスト実行 - レスポンスの型を明示的に定義
    const response = await apiRequest.get<{
      data: {
        organization: Organization;
        members: OrganizationMember[];
        agents: OrganizationAgent[];
      }
    }>(`admin/organizations/${id}`);
    
    console.log('adminOrganizationService: APIレスポンス受信', response);
    
    // レスポンスデータ確認
    if (!response || !response.data) {
      console.error('adminOrganizationService: レスポンスが空または無効', response);
      throw new Error('APIレスポンスが無効です');
    }
    
    // 結果を型に合わせて返す
    return response.data;
  } catch (error) {
    console.error('adminOrganizationService: 詳細取得エラー', error);
    // エラーの詳細なログ
    if (error.response) {
      // サーバーからのレスポンスがある場合
      console.error('adminOrganizationService: サーバーレスポンス', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      // リクエストは送信されたがレスポンスがない場合
      console.error('adminOrganizationService: リクエスト情報', error.request);
    } else {
      // リクエスト設定中のエラー
      console.error('adminOrganizationService: エラーメッセージ', error.message);
    }
    
    throw handleApiError(error, '組織詳細の取得に失敗しました');
  }
};

/**
 * 管理者用組織作成
 * @param data 組織データ
 */
export const createOrganization = async (data: {
  name: string;
  description?: string;
  member_limit: number;
  monthly_token_limit: number;
  max_concurrent_image_jobs?: number;
}): Promise<Organization> => {
  try {
    const response = await apiRequest.post('/admin/organizations', data);
    // APIレスポンスの構造に合わせて型アサーションを使用
    const responseData = response as unknown as { 
      data: { 
        data: {
          organization: Organization;
        } 
      } 
    };
    return responseData.data.data.organization;
  } catch (error) {
    throw handleApiError(error, '組織の作成に失敗しました');
  }
};

/**
 * 組織更新の応答から組織を取り出す。
 *
 * `apiRequest.put` はレスポンスボディをそのまま解決するので、正しい形は
 * `{ message, data: { organization } }`（OrganizationManagementController::update）。
 * 旧実装は `data.data.organization` を読んでいて undefined になっていたため、
 * 正の形を先に見つつ、旧い二重ネストと organization 直置きもフォールバックで受ける。
 */
const extractOrganization = (body: unknown): Organization => {
  const parsed = body as {
    data?: { organization?: Organization; data?: { organization?: Organization } };
    organization?: Organization;
  } | null | undefined;

  const organization =
    parsed?.data?.organization ?? parsed?.data?.data?.organization ?? parsed?.organization;

  if (!organization) {
    throw new Error('組織の更新に失敗しました（応答に組織情報が含まれていません）');
  }
  return organization;
};

/**
 * 管理者用組織更新
 * @param id 組織ID
 * @param data 更新データ
 */
export const updateOrganization = async (
  id: number,
  data: {
    name?: string;
    description?: string;
    plan?: PlanType | null;
    member_limit?: number;
    monthly_token_limit?: number;
    monthly_credit_limit?: number | null;
    enable_ai_knowledge?: boolean;
    enable_product_catalog?: boolean;
    enable_user_created_agents?: boolean;
    enable_video_material?: boolean;
    max_concurrent_image_jobs?: number;
    max_product_registrations?: number | null;
    enable_competitor_monitoring?: boolean;
    /** true で競合監視を停止（打刻）、false で再開（打刻を消す）。 */
    competitor_monitoring_disabled?: boolean;
  }
): Promise<Organization> => {
  try {
    const response = await apiRequest.put(`/admin/organizations/${id}`, data);
    return extractOrganization(response);
  } catch (error) {
    throw handleApiError(error, '組織の更新に失敗しました');
  }
};

/**
 * 管理者用 Stripe 契約情報の手動紐付け・解除
 * @param id 組織ID
 * @param data stripe_customer_id（cus_ / null=解除）・stripe_subscription_id（sub_）・apply_plan_provisioning
 */
export const linkOrganizationStripe = async (
  id: number,
  data: {
    stripe_customer_id: string | null;
    stripe_subscription_id?: string | null;
    apply_plan_provisioning?: boolean;
  }
): Promise<Organization> => {
  try {
    const response = await apiRequest.put(`/admin/organizations/${id}/stripe-link`, data);
    const body = response as unknown as { data: { organization: Organization }; message?: string };
    return body.data.organization;
  } catch (error) {
    throw handleApiError(error, 'Stripe連携の更新に失敗しました');
  }
};

/** トライアル延長APIの戻り値 */
export interface TrialExtensionResult {
  trial_started_at: string | null;
  trial_ends_at: string | null;
  is_in_trial: boolean;
  trial_has_expired: boolean;
}

/**
 * 管理者用トライアル期間の延長
 *
 * days と trial_ends_at はどちらか一方を指定する。
 * - days: 有効中なら現在の終了日、期限切れ・未開始なら現在時刻を起点に加算
 * - trial_ends_at: YYYY-MM-DD 形式。その日の終わり（23:59:59）まで有効になる
 *
 * @param id 組織ID
 */
export const extendOrganizationTrial = async (
  id: number,
  data: { days?: number; trial_ends_at?: string }
): Promise<TrialExtensionResult> => {
  try {
    const response = await apiRequest.post(`/admin/organizations/${id}/extend-trial`, data);
    const body = response as unknown as { data: TrialExtensionResult; message?: string };
    return body.data;
  } catch (error) {
    throw handleApiError(error, 'トライアル期間の延長に失敗しました');
  }
};

/**
 * 管理者用組織削除
 * @param id 組織ID
 */
export const deleteOrganization = async (id: number): Promise<void> => {
  try {
    await apiRequest.delete(`/admin/organizations/${id}`);
  } catch (error) {
    throw handleApiError(error, '組織の削除に失敗しました');
  }
};

/**
 * 管理者用組織メンバー追加・更新
 * @param organizationId 組織ID
 * @param data メンバーデータ
 */
export const addOrUpdateOrganizationMember = async (
  organizationId: number,
  data: {
    user_id?: number;
    name: string;
    email: string;
    role: 'leader' | 'member';
    password?: string;
  }
): Promise<{ data: { member: OrganizationMember; generated_password?: string } }> => {
  try {
    const response = await apiRequest.post(`/admin/organizations/${organizationId}/members`, data);
    // APIレスポンスの構造に合わせて型アサーションを使用
    const responseData = response as unknown as {
      data: {
        data: {
          member: OrganizationMember;
          generated_password?: string;
        }
      }
    };
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, 'メンバーの追加・更新に失敗しました');
  }
};

/**
 * 管理者用組織メンバー削除
 * @param organizationId 組織ID
 * @param userId ユーザーID
 */
export const removeOrganizationMember = async (organizationId: number, userId: number): Promise<void> => {
  try {
    await apiRequest.delete(`/admin/organizations/${organizationId}/members/${userId}`);
  } catch (error) {
    throw handleApiError(error, 'メンバーの削除に失敗しました');
  }
};

/**
 * 管理者用組織のAIエージェント設定更新
 * @param organizationId 組織ID
 * @param settings エージェント設定
 */
export const updateOrganizationAgentSettings = async (
  organizationId: number,
  settings: AgentSettingsUpdateRequest
): Promise<void> => {
  try {
    await apiRequest.put(`/admin/organizations/${organizationId}/agent-settings`, settings);
  } catch (error) {
    throw handleApiError(error, 'AIエージェント設定の更新に失敗しました');
  }
};

/**
 * 管理者用組織の有効/無効状態を切り替える
 * @param organizationId 組織ID
 * @param isActive 有効にする場合はtrue、無効にする場合はfalse
 */
export const toggleOrganizationActive = async (
  organizationId: number,
  isActive: boolean
): Promise<Organization> => {
  try {
    const response = await apiRequest.patch(`/admin/organizations/${organizationId}/toggle-active`, {
      is_active: isActive
    });
    
    // レスポンスのデバッグログを追加
    console.log('toggleOrganizationActive: レスポンス受信', response);
    
    // APIレスポンスの構造に合わせて型アサーションを使用
    const typedResponse = response as any;
    
    // 直接レスポンスにorganizationが含まれている場合
    if (typedResponse && typedResponse.organization) {
      console.log('toggleOrganizationActive: 組織データ取得成功', typedResponse.organization);
      return typedResponse.organization as Organization;
    }
    // axiosConfigの標準的な処理でdata内にorganizationがある場合
    else if (typedResponse && typedResponse.data && typedResponse.data.organization) {
      console.log('toggleOrganizationActive: 組織データ取得成功 (data内)', typedResponse.data.organization);
      return typedResponse.data.organization as Organization;
    } 
    else {
      console.error('toggleOrganizationActive: レスポンス構造が期待と異なります', response);
      throw new Error('APIのレスポンス形式が期待と異なります');
    }
  } catch (error) {
    throw handleApiError(error, isActive ? '組織の有効化に失敗しました' : '組織の無効化に失敗しました');
  }
};

/**
 * 管理者用組織ロゴアップロード
 * @param organizationId 組織ID
 * @param logoFile ロゴファイル
 */
export const uploadOrganizationLogo = async (
  organizationId: number,
  logoFile: File
): Promise<{
  organization: {
    id: number;
    name: string;
    logo_path: string;
    logo_url: string;
  };
}> => {
  try {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response = await apiRequest.post(`/admin/organizations/${organizationId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // APIレスポンスの構造に合わせて型アサーションを使用
    const responseData = response as unknown as { 
      data: { 
        data: {
          organization: {
            id: number;
            name: string;
            logo_path: string;
            logo_url: string;
          };
        } 
      } 
    };
    return responseData.data.data;
  } catch (error) {
    throw handleApiError(error, 'ロゴのアップロードに失敗しました');
  }
};

/**
 * 管理者用組織ロゴ削除
 * @param organizationId 組織ID
 */
export const deleteOrganizationLogo = async (
  organizationId: number
): Promise<{
  organization: {
    id: number;
    name: string;
    logo_path: null;
  };
}> => {
  try {
    const response = await apiRequest.delete(`/admin/organizations/${organizationId}/logo`);
    
    // APIレスポンスの構造に合わせて型アサーションを使用
    const responseData = response as unknown as { 
      data: { 
        data: {
          organization: {
            id: number;
            name: string;
            logo_path: null;
          };
        } 
      } 
    };
    return responseData.data.data;
  } catch (error) {
    throw handleApiError(error, 'ロゴの削除に失敗しました');
  }
};

/**
 * 組織のクレジット残高を取得する
 */
export const fetchOrganizationCredits = async (
  organizationId: number
): Promise<{ wallets: CreditWalletInfo; total_balance: number }> => {
  try {
    const response = await apiRequest.get(`admin/organizations/${organizationId}/credits`);
    const responseData = response as unknown as ApiResponse<{ wallets: CreditWalletInfo; total_balance: number }>;
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, 'クレジット残高の取得に失敗しました');
  }
};

/**
 * 組織に追加クレジットを付与する
 */
export const grantOrganizationCredits = async (
  organizationId: number,
  data: { amount: number; description: string }
): Promise<{ additional_balance: number; total_balance: number }> => {
  try {
    const response = await apiRequest.post(`admin/organizations/${organizationId}/credits/grant`, data);
    const responseData = response as unknown as { data: { additional_balance: number; total_balance: number }; message: string };
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, '追加クレジットの付与に失敗しました');
  }
};

/**
 * 組織のクレジット設定を更新する
 */
export const updateOrganizationCreditSettings = async (
  organizationId: number,
  data: { monthly_credit_limit?: number; daily_free_credits?: number }
): Promise<{ monthly_credit_limit: number; daily_free_credits: number; changes: string[] }> => {
  try {
    const response = await apiRequest.put(`admin/organizations/${organizationId}/credits/settings`, data);
    const responseData = response as unknown as { data: { monthly_credit_limit: number; daily_free_credits: number; changes: string[] }; message: string };
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, 'クレジット設定の更新に失敗しました');
  }
};

/**
 * 組織のクレジット取引履歴を取得する
 */
export const fetchOrganizationCreditTransactions = async (
  organizationId: number,
  params?: { per_page?: number; page?: number; wallet_type?: string; transaction_type?: string; year_month?: string }
): Promise<{
  data: CreditTransaction[];
  current_page: number;
  last_page: number;
  total: number;
}> => {
  try {
    const response = await apiRequest.get(`admin/organizations/${organizationId}/credits/transactions`, {
      params
    });
    const responseData = response as unknown as ApiResponse<{
      data: CreditTransaction[];
      current_page: number;
      last_page: number;
      total: number;
    }>;
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, 'クレジット取引履歴の取得に失敗しました');
  }
};

export interface MonthlySummary {
  month: string;
  consumed: number;
  granted: number;
}

export const fetchOrganizationCreditMonthlySummary = async (
  organizationId: number
): Promise<MonthlySummary[]> => {
  try {
    const response = await apiRequest.get(`admin/organizations/${organizationId}/credits/monthly-summary`);
    const responseData = response as unknown as ApiResponse<MonthlySummary[]>;
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, '月別クレジットサマリーの取得に失敗しました');
  }
};

/**
 * 組織の使用統計データを取得する
 */
export const fetchOrganizationUsageStatistics = async (
  organizationId: number, 
  period: number = 30
): Promise<import('../../types').OrganizationUsageData> => {
  try {
    const response = await apiRequest.get(`admin/organizations/${organizationId}/usage-statistics`, {
      params: { period }
    });
    
    // APIレスポンスの構造に合わせて型アサーションを使用
    const responseData = response as unknown as ApiResponse<import('../../types').OrganizationUsageData>;
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, '使用統計データの取得に失敗しました');
  }
};
